import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    DKLabel,
    DKButton,
    DKInput,
    INPUT_TYPE,
    INPUT_VIEW_DIRECTION,
    showLoader,
    removeLoader,
    showToast,
    TOAST_TYPE,
    Toggle,
    DKTooltipWrapper,
    DKIcons,
    DKIcon
} from "deskera-ui-library";
import Popup from "../../common/Popup";
import Stepper from "../../common/Stepper";
import USComplianceService from "../../../services/compliance/US/USComplianceService";
import ImportScript from "../../checkhq/ImportScript";
import BankAccountService from "../../../services/BankAccountService";
import MessageInfo from "../../common/MessageInfo";
import { PROFILE_STAGES_CONSTANT } from "../../../constants/Constant";
import ProfileSetupService from "../../../services/ProfileSetup-service";
import TenantManager from "../../../managers/TenantManager";

function AddBankAccountUS(props) {
    const { t } = useTranslation();

    const [routingNumber, setRoutingNumber] = useState({
        id: "routingNumber",
        title: t("ROUTING_NUMBER"),
        type: INPUT_TYPE.NUMBER,
        tooltip: t("US_ROUTING_NUMBER_TOOLTIP"),
        validation: [["required"], ["pattern", "^(0[1-9]|1[0-2]|2[1-9]|3[0-2])([0-9]{7})$"]]
    });

    const [accountNumber, setAccountNumber] = useState({
        id: "accountNumber",
        title: t("ACCOUNT_NUMBER"),
        type: INPUT_TYPE.NUMBER,
        tooltip: t("US_ACCOUNT_NUMBER_TOOLTIP"),
        validation: [["required"], ["minLength", 4], ["maxLength", 17]]
    });

    const [bank, setBank] = useState({
        id: "bank",
        title: t("BANK_NAME"),
        type: INPUT_TYPE.TEXT,
        validation: []
    });

    const [bankAccount, setBankAccount] = useState(null);
    const [steps, setSteps] = useState(null);
    const [selectedStep, setSelectedStep] = useState(0);
    const [onStepSelect, setOnStepSelect] = useState(0);
    const [isEdit, setIsEdit] = useState(false);
    const [isLaunchPlaid, setIsLaunchPlaid] = useState(true);
    const [isAuthenticateWithPlaid, setIsAuthenticateWithPlaid] = useState(true);

    useEffect(() => {
        if (BankAccountService.BANK_ACCOUNT) {
            setBankAccount(BankAccountService.BANK_ACCOUNT);
            setIsEdit(true);
            BankAccountService.BANK_ACCOUNT = null;
        } else {
            setBankAccount(null);
        }

        setSteps([
            { title: t("BANK_DETAILS"), onClick: () => { setOnStepSelect(0) } },
            { title: t("CONFIRMATION"), onClick: () => { setOnStepSelect(1) } }
        ]);
    }, []);

    useEffect(() => {
        switch (onStepSelect) {
            case 0:
                setSelectedStep(0);
                break;
            case 1:
                if (!isInvalid()) {
                    setSelectedStep(1);
                }
                break;
            default:
                break;
        }
    }, [onStepSelect]);

    useEffect(() => {
        if (bankAccount) {
            setBankAccountDetails();
        }
    }, [bankAccount]);

    useEffect(() => {
        if (!isAuthenticateWithPlaid) {
            setBank({
                ...bank,
                validation: [["required"], ["maxLength", 64]]
            });
        } else {
            setBank({
                ...bank,
                validation: []
            });
        }
    }, [isAuthenticateWithPlaid]);

    const setBankAccountDetails = () => {
        setRoutingNumber({
            ...routingNumber,
            value: bankAccount.routingNumber
        });

        setAccountNumber({
            ...accountNumber,
            value: bankAccount.accountNumber
        });

        setBank({
            ...bank,
            value: bankAccount.bank
        });

        setIsAuthenticateWithPlaid(bankAccount.isVerified);
    }

    const showError = (errorMessage) => {
        if (errorMessage) {
            return (
                <div style={{ marginTop: "-19px", marginBottom: "8px" }}>
                    <DKLabel className="ml-xs fs-s text-red" text={errorMessage} />
                </div>
            )
        }
    }

    const hasError = (field, value) => {
        let error = {
            invalid: false,
            errorMessage: null
        };

        const validation = field.validation;

        if (validation && validation.length !== 0) {
            for (let v of validation) {
                switch (v[0]) {
                    case "required":
                        if (value === null || value === undefined || String(value).trim() === "") {
                            error.invalid = true;
                        }
                        break;
                    case "pattern":
                        if (value && !new RegExp(v[1]).test(value)) {
                            error.invalid = true;
                            error.errorMessage = `Invalid ${field.title}`;
                        }
                        break;
                    case "minLength":
                        if (value && String(value).length < v[1]) {
                            error.invalid = true;
                            error.errorMessage = `${field.title} should not less than ${v[1]}`;
                        }
                        break;
                    case "maxLength":
                        if (value && String(value).length > v[1]) {
                            error.invalid = true;
                            error.errorMessage = `${field.title} should not greater than ${v[1]}`;
                        }
                        break;
                    default:
                        error.invalid = false;
                        error.errorMessage = null;
                        break;
                }

                if (error.invalid) {
                    break;
                }
            }
        }

        return error;
    }

    const isInvalid = () => {
        let invalid = false;

        const fields = [
            { field: { ...routingNumber }, setField: setRoutingNumber },
            { field: { ...accountNumber }, setField: setAccountNumber },
            { field: { ...bank }, setField: setBank }
        ];

        fields.forEach((object) => {
            const error = hasError(object.field, object.field.value);
            object.field.invalid = error.invalid;
            object.field.errorMessage = error.errorMessage;

            if (object.field.invalid) {
                invalid = true;
            }

            object.setField(object.field);
        });

        return invalid;
    }

    const getAccountPayload = () => {
        const payload = {
            routingNumber: routingNumber.value,
            accountNumber: accountNumber.value
        }

        if (isEdit) {
            payload["edited"] = isEdit;
            payload["tenantBankId"] = "";
        }

        return payload;
    }

    const onNext = () => {
        if (isInvalid()) {
            return;
        }

        if (isAuthenticateWithPlaid) {
            authenticateWithPlaid();
        } else {
            saveBankAccount();
        }
    }

    const authenticateWithPlaid = () => {
        const accountPayload = getAccountPayload();
        showLoader();
        USComplianceService.createLinkToken(accountPayload).then(
            (response) => {
                if (response['launchPlaid']) {
                    setIsLaunchPlaid(true);
                    if (window.Plaid) {
                        removeLoader();
                        const handler = window.Plaid.create({
                            token: response['linkToken'],
                            onSuccess: (publicToken, metadata) => {
                                showLoader(t("PLEASE_WAIT_1"));
                                const accessTokenPayload = {
                                    publicToken: publicToken,
                                    accountNumber: accountPayload.accountNumber,
                                    routingNumber: accountPayload.routingNumber
                                };
                                USComplianceService.setAccessToken(accessTokenPayload).then(
                                    () => {
                                        const verifyAccountPayload = {
                                            ...accountPayload,
                                            bank: metadata.institution.name
                                        }
                                        USComplianceService.verifyAccount(verifyAccountPayload).then(
                                            (account) => {
                                                removeLoader();
                                                if (account && account['verified']) {
                                                    setSelectedStep(1);
                                                    setBankAccount(account);
                                                    updateStateOfProfileStage();
                                                } else {
                                                    showToast(t("UNABLE_TO_VERIFY"), TOAST_TYPE.FAILURE);
                                                    handler.destroy();
                                                }
                                            }, (error) => {
                                                removeLoader();
                                                showToast(t("FAILED_TO_VERIFY"), TOAST_TYPE.FAILURE);
                                            }
                                        );
                                    }, () => {
                                        removeLoader();
                                        showToast(t("FAILED_TO_SET_ACCESS_TOKEN"), TOAST_TYPE.FAILURE);
                                    }
                                );
                            },
                            onEvent: (eventName) => {
                                if (eventName === 'HANDOFF' || eventName === 'EXIT') {
                                    handler.destroy();
                                }
                            }
                        });

                        handler.open();
                    } else {
                        removeLoader();
                        showToast(t("FAILED_LINKED_TOKEN"), TOAST_TYPE.FAILURE);
                    }
                } else {
                    removeLoader();
                    setBankAccount(response);
                    setIsLaunchPlaid(false);
                    setSelectedStep(1);

                    if (response["tenantBankId"]) {
                        updateStateOfProfileStage();
                    }
                }
            }, (error) => {
                removeLoader();
                showToast(t("FAILED_LINKED_TOKEN"), TOAST_TYPE.FAILURE);
            }
        );
    }

    const saveBankAccount = () => {
        const payload = {
            routingNumber: routingNumber.value,
            accountNumber: accountNumber.value,
            bank: bank.value,
            accountType: "BANK"
        }

        if (bankAccount) {
            showLoader(t("UPDATING_BANK_ACCOUNT"));
            BankAccountService.updateBankAccount(bankAccount.id, payload).then(
                (response) => {
                    removeLoader();
                    showToast(t("BANK_UPDATE_SUCCESS"), TOAST_TYPE.SUCCESS);
                    setSelectedStep(1);
                    setBankAccount(response);
                }, (error) => {
                    removeLoader();
                    if (error?.errorMessage) {
                        showToast(error.errorMessage, TOAST_TYPE.FAILURE);
                    } else {
                        showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
                    }
                }
            );
        } else {
            showLoader(t("SAVING_BANK_ACCOUNT"));
            BankAccountService.saveBankAccount(payload).then(
                (response) => {
                    removeLoader();
                    showToast(t("BANK_SAVE_SUCCESS"), TOAST_TYPE.SUCCESS);
                    setSelectedStep(1);
                    setBankAccount(response);
                    updateStateOfProfileStage();
                }, (error) => {
                    removeLoader();
                    if (error?.errorMessage) {
                        showToast(error.errorMessage, TOAST_TYPE.FAILURE);
                    } else {
                        showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
                    }
                }
            );
        }
    }

    const updateStateOfProfileStage = () => {
        ProfileSetupService.updateStateOfProfileStage(PROFILE_STAGES_CONSTANT.BANK_DETAILS).then(
            (response) => {
                TenantManager.setProfileData(response['profileStages']);
            }
        );
    }

    const bankDetailsStep = () => {
        return (
            <div>
                <div style={{ flex: "100%", maxWidth: "100%" }} className={`${isEdit ? "disable-button" : ""}`}>
                    <div className="row pt-l pb-m mb-s position-relative">
                        <Toggle
                            isOn={isAuthenticateWithPlaid}
                            onChange={() => {
                                setIsAuthenticateWithPlaid(!isAuthenticateWithPlaid);
                            }}
                        />
                        <DKLabel className="mb-xs m-h-s" text={t("AUTH_WITH_PLAID")} />
                        <DKTooltipWrapper content={t("PLAID_VERIFY_TOOLTIP")} tooltipClassName="warning-tooltip-prop">
                            <DKIcon src={DKIcons.ic_info} className="ic-s cursor-hand" style={{ opacity: 0.4 }} />
                        </DKTooltipWrapper>
                    </div>
                </div>
                <div style={{ flex: "100%", maxWidth: "100%" }}>
                    <div className="row pb-l mb-s position-relative">
                        <DKInput
                            title={routingNumber.title}
                            type={routingNumber.type}
                            direction={INPUT_VIEW_DIRECTION.VERTICAL}
                            value={routingNumber.value}
                            errorMessage={routingNumber.errorMessage}
                            readOnly={!!routingNumber.readOnly}
                            required={true}
                            tooltip={{
                                content: routingNumber.tooltip,
                                className: "warning-tooltip-prop"
                            }}
                            invalid={routingNumber.invalid}
                            canValidate={routingNumber.invalid}
                            onChange={(value) => {
                                setRoutingNumber({
                                    ...routingNumber,
                                    value: value,
                                    ...hasError(routingNumber, value)
                                });
                            }}
                            validator={routingNumber.validator}
                        />
                    </div>
                    {routingNumber.invalid && showError(routingNumber.errorMessage)}
                </div>
                <div style={{ flex: "100%", maxWidth: "100%" }}>
                    <div className="row pb-l mb-s position-relative">
                        <DKInput
                            title={accountNumber.title}
                            type={accountNumber.type}
                            direction={INPUT_VIEW_DIRECTION.VERTICAL}
                            value={accountNumber.value}
                            errorMessage={accountNumber.errorMessage}
                            readOnly={!!accountNumber.readOnly}
                            required={true}
                            tooltip={{
                                content: accountNumber.tooltip,
                                className: "warning-tooltip-prop"
                            }}
                            invalid={accountNumber.invalid}
                            canValidate={accountNumber.invalid}
                            onChange={(value) => {
                                setAccountNumber({
                                    ...accountNumber,
                                    value: value,
                                    ...hasError(accountNumber, value)
                                });
                            }}
                            validator={accountNumber.validator}
                        />
                    </div>
                    {accountNumber.invalid && showError(accountNumber.errorMessage)}
                </div>
                <div style={{ flex: "100%", maxWidth: "100%" }}>
                    <div className="row pb-l mb-s position-relative">
                        <DKInput
                            title={bank.title}
                            type={bank.type}
                            direction={INPUT_VIEW_DIRECTION.VERTICAL}
                            value={bank.value}
                            errorMessage={bank.errorMessage}
                            readOnly={!!bank.readOnly}
                            required={!isAuthenticateWithPlaid}
                            tooltip={{
                                content: bank.tooltip,
                                className: "warning-tooltip-prop"
                            }}
                            invalid={bank.invalid}
                            canValidate={bank.invalid}
                            onChange={(value) => {
                                setBank({
                                    ...bank,
                                    value: value,
                                    ...hasError(bank, value)
                                });
                            }}
                            validator={bank.validator}
                        />
                    </div>
                    {bank.invalid && showError(bank.errorMessage)}
                </div>
            </div>
        );
    }

    const confirmationStep = () => {
        return (
            <div>
                <div className="mb-l">
                    <DKLabel text={t("ROUTING_NUMBER")} className="ic-s fw-b" />
                    <DKLabel text={bankAccount?.routingNumber} className="ic-s" />
                </div>
                <div className="mb-l">
                    <DKLabel text={t("BANK_NAME")} className="ic-s fw-b" />
                    <DKLabel text={bankAccount?.bank} className="ic-s" />
                </div>
                <div className="mb-l">
                    <DKLabel text={t("ACCOUNT_NUMBER")} className="ic-s fw-b" />
                    <DKLabel text={bankAccount?.accountNumber} className="ic-s" />
                </div>
            </div>
        );
    }

    const renderHeaderForStep = () => {
        return (
            <div className="row justify-content-between p-h-xxl p-v-m bg-gray2">
                <DKLabel text={t("BANK_DETAILS")} className="fs-l fw-m" />
                <DKButton
                    title={"Close"}
                    className="border-m bg-white ml-auto"
                    onClick={() => {
                        props.onClose();
                    }}
                />
            </div>
        )
    }

    const renderBankAccountForm = () => {
        return (
            <div className="parent-width">
                {!props.renderAsStep &&
                    <div className="row">
                        <DKLabel text={bankAccount ? t("EDIT_BANK_ACCOUNT") : t("ADD_NEW_BANK_ACCOUNT")} className="fs-l fw-b" />
                    </div>
                }
                {steps?.length > 0 &&
                    <Fragment>
                        <div className="mt-xxl">
                            <Stepper
                                data={steps}
                                currentIndex={selectedStep}
                                currentStep={selectedStep}
                                stepperType="BANK_DETAILS"
                                width={"12vw"}
                            />
                        </div>
                        <div className="mt-l" style={{ minHeight: "165px" }}>
                            {selectedStep === 0 && bankDetailsStep()}
                            {selectedStep === 1 &&
                                <Fragment>
                                    {!isLaunchPlaid && <MessageInfo message={[{ message: t("BANK_ALREADY_LINKED") }]} bannerClass="mb-l" />}
                                    {confirmationStep()}
                                </Fragment>
                            }
                        </div>
                    </Fragment>
                }
                <div className="row">
                    <div className="ml-auto d-flex">
                        {selectedStep === 0 &&
                            <Fragment>
                                {!props.renderAsStep && <DKButton
                                    title={t("CANCEL")}
                                    className="border-radius-m bg-gray1 border-m"
                                    onClick={() => {
                                        props.onClose();
                                    }}
                                />}
                                <DKButton
                                    title={t("NEXT")}
                                    className="bg-app text-white ml-m"
                                    onClick={() => {
                                        onNext();
                                    }}
                                />
                            </Fragment>
                        }
                        {selectedStep === 1 &&
                            <DKButton
                                title={props.renderAsStep ? "Save & Next" : t("CLOSE")}
                                className="bg-app text-white ml-m"
                                onClick={() => {
                                    if (props.renderAsStep) {
                                        props.onSaveAndNext();
                                    } else {
                                        props.onClose(true);
                                    }
                                }}
                            />
                        }
                    </div>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <ImportScript src="https://cdn.plaid.com/link/v2/stable/link-initialize.js" />
            {props.renderAsStep && (
                <div>
                    {renderHeaderForStep()}
                    <div className="d-flex justify-content-center mt-xxl">
                        <div style={{ minWidth: "35%", maxWidth: "35%" }}>
                            {renderBankAccountForm()}
                        </div>
                    </div>
                </div>
            )
            }
            {!props.renderAsStep &&
                <Popup popupWindowStyles={{ minWidth: "550px" }}>
                    {renderBankAccountForm()}
                </Popup>
            }
        </Fragment>
    );
}

export default AddBankAccountUS;
